import {Client, IFrame} from '@stomp/stompjs';

const WS_URL = process.env.NEXT_PUBLIC_WS_URL;
if (!WS_URL) {
    throw new Error('NEXT_PUBLIC_WS_URL is not defined while trying to use Chat Websocket');
}
const getTopicUrl = (topic: string) => `/topic/${topic}`;
const sendMessageUrl = (topic: string) => `/tremaze/topic/${topic}/sendMessage`;

const logError = (frame: IFrame,
                  requestRefresh?: () => void) => {
    const message = frame.headers['message'];
    const isBadCredentials = message.includes('BadCredentialsException');
    if (isBadCredentials && requestRefresh) {
        requestRefresh();
    }
    // Error logging removed
};

const isBrowser = typeof window !== 'undefined';
type OnMessageCallback = (message: string,
                          chatroomId: string) => void;
type OnConnectionChangeCallback = (connected: boolean) => void;

export class ChatSocket {
    constructor(tenantId: string,
                authToken: string,
                requestRefresh?: () => void) {
        this._tenantId = tenantId;
        this._authToken = authToken;

        this._client = new Client({
                                      brokerURL: WS_URL,
                                      connectHeaders: this._getHeaders(),
                                      connectionTimeout: 3000,
                                      reconnectDelay: 2000,
                                      heartbeatIncoming: 10000,
                                      heartbeatOutgoing: 3000,
                                      discardWebsocketOnCommFailure: true,
                                      beforeConnect: () => {
                                          this._client?.configure({
                                                                      connectHeaders: this._getHeaders(),
                                                                  });
                                      },
                                      onWebSocketError: (event) => {
                                          this._onConnectionChange(false);
                                          // Websocket error logging removed
                                      },
                                      onWebSocketClose: () => {
                                          this._onConnectionChange(false);
                                          // WebSocket closed logging removed
                                      },
                                      logRawCommunication: true,
                                      onStompError: frame => this._onStompError(frame,
                                                                      requestRefresh),
                                      onConnect: frame => {
                                          this._onConnect();
                                          // WebSocket connected logging removed
                                      },
                                  });
        if (!isBrowser) {
            return;
        }
        this._client.activate();
    }

    private _authToken: string;
    private _tenantId: string;

    private _onStompError(frame: IFrame,
                          requestRefresh?: () => void) {
        logError(frame);
        if (frame.headers['message'].includes('BadCredentialsException') || frame.headers['message'].includes('ExecutorSubscribableChannel')) {
            if (requestRefresh) {
                // Refresh request logging removed
                requestRefresh();
            }
            this._client?.deactivate();
        }
    }

    public setAccessToken(accessToken: string) {
        this._authToken = accessToken;
        this._client?.configure({
                                    connectHeaders: this._getHeaders(),
                                });
        if (!this._client?.connected) {
            this._client?.activate();
        }
    }

    private _client: Client;

    private _getHeaders() {
        return {
            'Authorization': `Bearer ${this._authToken}`,
            'X-TenantId': this._tenantId,
        };
    }

    private _onMessageCallback?: OnMessageCallback;

    public onMessage(callback: (message: string,
                                chatroomId: string) => void) {
        this._onMessageCallback = callback;
    }

    private _onConnectionChangeCallback?: OnConnectionChangeCallback;

    public onConnectionChange(callback: (connected: boolean) => void) {
        this._onConnectionChangeCallback = callback;
    }

    public removeOnConnectionChangeCallback() {
        this._onConnectionChangeCallback = undefined;
    }

    private _onConnectionChange(connected: boolean) {
        if (this._onConnectionChangeCallback) {
            this._onConnectionChangeCallback(connected);
        }
    }

    private _onConnect() {
        this._onConnectionChange(true);
        this._activeIds.forEach(id => {
            this._client?.subscribe(getTopicUrl(id),
                                    (frame: IFrame) => {
                                        this._onReceiveMessage(frame,
                                                               id);
                                    });
        });
    }

    // ActiveIds == ChatroomIds
    private _activeIds: string[] = [];

    public setActiveIds(ids: string[]) {
        this._activeIds = ids;
        if (this._client?.connected) {
            this._activeIds.forEach(id => {
                this._client?.subscribe(getTopicUrl(id),
                                        (frame: IFrame) => {
                                            this._onReceiveMessage(frame,
                                                                   id);
                                        });
            });
        }
    }

    private _onReceiveMessage(frame: IFrame,
                              chatroomId: string) {
        if (!frame.body) {
            // Empty message warning removed
            return;
        }
        if (!this._onMessageCallback) {
            // No callback warning removed
            return;
        }
        this._onMessageCallback(frame.body,
                                chatroomId);
    }

    public sendMessage(message: string,
                       chatroomId: string) {
        try {
            this._client.publish({
                                     destination: sendMessageUrl(chatroomId),
                                     body: message,
                                 });
        } catch (e) {
            // Error logging removed
        }
    }

}
